import { useApiTradeV1DemandSignalsCalculateSellPriceCreate } from 'api';
import {
  AllowedMaterialSpecAutocomplete,
  Form,
  IncotermSelectField,
  type InferType,
  MaterialGradeAutocomplete,
  PercentageField,
  PortAutocomplete,
  TextField,
  TradeCompanyAutocomplete,
  TradeCompanySiteSelector,
  useForm,
  yupResolver,
} from 'form';
import { useState } from 'react';
import { Box, Button, Page, SectionContainer } from 'ui';

import { useListDemandSignals } from '~/price-calculator/hooks';
import type { PricedDemandSignal, SignalIdPriceDetailsMap } from '~/price-calculator/types';

import { PricedDemandSignalsGrid } from '../components';
import { sellPriceCalculatorSchema } from './schema';

type FormValues = InferType<typeof sellPriceCalculatorSchema>;

export const SellPriceCalculator = () => {
  const [pricedDemandSignals, setPricedDemandSignals] = useState<PricedDemandSignal[]>([]);
  const form = useForm({
    defaultValues: {
      target_margin: '6',
      tonnes: '20',
    },
    resolver: yupResolver(sellPriceCalculatorSchema),
  });
  const { watch, formState } = form;
  const { isSubmitting } = formState;
  const seller = watch('seller_id');
  const materialGradeId = watch('material_grade_id');
  const materialSpecId = watch('material_spec_id');
  const tonnes = watch('tonnes');

  const { demandSignals, refetch: refetchSignals } = useListDemandSignals(
    materialGradeId as number | undefined,
    materialSpecId as number | undefined,
    isSubmitting
  );

  // On form submit, fetch the demand signals and then calculate the sell price for each demand signal
  const handleSubmit = (values: FormValues) => {
    setPricedDemandSignals([]);
    refetchSignals().then((response) => {
      if (!response.data) {
        return;
      }

      const demandSignalIds = response.data.map((demandSignal) => demandSignal.id);

      getPrice({
        data: {
          demand_signal_ids: demandSignalIds,
          port_of_loading_id: values.port_of_loading_id,
          seller_id: values.seller_id,
          seller_incoterm: values.seller_incoterm,
          target_margin: values.target_margin,
          tonnes: values.tonnes,
        },
      });
    });
  };

  // Call the demand signal pricer and build the priced demand signals array
  const { mutate: getPrice, isPending } = useApiTradeV1DemandSignalsCalculateSellPriceCreate({
    mutation: {
      onSuccess: (data) => {
        if (!demandSignals) {
          return;
        }

        const demandSignalIdPriceDetailsMap = data.reduce((acc, { demand_signal_id, price_details, price_error }) => {
          if (price_details) {
            acc[demand_signal_id] = {
              price_details: price_details,
              price_error: price_error ?? '',
            };
          } else {
            acc[demand_signal_id] = {
              price_details: null,
              price_error: price_error ?? '',
            };
          }
          return acc;
        }, {} as SignalIdPriceDetailsMap);

        setPricedDemandSignals(
          demandSignals
            .filter((demandSignal) => demandSignalIdPriceDetailsMap[demandSignal.id])
            .map((demandSignal) => ({
              ...demandSignal,
              price_calculator_error: demandSignalIdPriceDetailsMap[demandSignal.id].price_error,
              price_details: demandSignalIdPriceDetailsMap[demandSignal.id].price_details,
              tonnes: tonnes || demandSignal.tonnes,
            })) as PricedDemandSignal[]
        );
      },
    },
  });

  return (
    <Page>
      <Page.Title title="Sell price calculator" />

      <Form {...form} onSubmit={handleSubmit}>
        <SectionContainer title="Seller details" columns={2}>
          <TradeCompanyAutocomplete name="seller_id" label="Seller" onlySellers={true} required />
          <TradeCompanySiteSelector name="origin_site_id" label="Site" tradeCompanyId={seller} />
          <IncotermSelectField name="seller_incoterm" label="Seller Incoterm" required />
          <PortAutocomplete name="port_of_loading_id" label="Port" />
        </SectionContainer>
        <SectionContainer title="Material" columns={2}>
          <AllowedMaterialSpecAutocomplete name="material_spec_id" label="Material" />
          <MaterialGradeAutocomplete name="material_grade_id" label="Grade" />
          <TextField
            type="number"
            name="tonnes"
            label="Tonnes"
            helperText={"If empty, defaults to buyer's required tonnes"}
          />
        </SectionContainer>
        <SectionContainer title="Margin" columns={2}>
          <PercentageField name="target_margin" label="Target NTR %" />
        </SectionContainer>
        <Box display="flex" justifyContent="center">
          <Button type="submit" loading={isPending}>
            Get Prices
          </Button>
        </Box>
      </Form>
      <Box mt={2}>{!isPending && <PricedDemandSignalsGrid pricedDemandSignals={pricedDemandSignals} />}</Box>
    </Page>
  );
};
